import React from "react";
import styled from "styled-components";
import smoothscroll from "smoothscroll-polyfill";
import throttle from "lodash/throttle";

// Services
import intl from "../../services/intl";
import { navigate } from "../../services/navigation";

// Utils
import NameMap from "../../utils/nameMap";
import { Color, rem, Font, responsive } from "../../utils/style";
import { variation } from "../../utils/launchDarkly";
import { getPlanIdForProductSku } from "../../utils/planToProduct";
import metrics from "../../utils/metrics";

// Components
import Img from "gatsby-image";
import RitualButton from "../global/RitualButton";
import CheckoutButton from "./CheckoutButton";
import MagicLink from "../MagicLink";
import ScrollableOverflow from "../ScrollableOverflow";
import Text from "../Text";

// Store
import { connect } from "react-redux";
import { addProductToCart } from "../../store/cart/actions";

// Styled Elements
const Grid = styled.div.attrs({
  id: "ProductLandingCards",
})`
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 80px;
  position: relative;
  padding-top: 38px;

  ${responsive.sm`
    padding-top: 0;
  `};

  ${responsive.md`
    margin-bottom: 120px;
  `}
`;

const MobileNavigation = styled.div`
  position: absolute;
  top: 0;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;

  overflow: visible;

  ${responsive.sm`
    display: none;
  `};

  &.fixed {
    position: fixed;
    top: 52px;
    right: 0px;
    z-index: 10;
    background-color: ${Color.white};
    padding: 8px 36px 8px;
  }
`;

const MobileLabel = styled.span`
  font-size: ${rem(12)};
  line-height: ${rem(20)};
  letter-spacing: 0.8px;
  font-weight: 500;
  opacity: 0.56;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  user-select: none;
  margin-right: 32px;

  &:last-of-type {
    margin-right: 0;
  }

  &.active {
    opacity: 1;
    border-bottom: 2px solid ${Color.ritualBlue};
  }
`;

// Height of Ritual nav + label nav
const mobileNavigationBuffer = 52 + 38;

export class ProductCards extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedIndex: 0,
      floatNavigation: false,
    };

    this.throttledOnScroll = throttle(this.onScroll.bind(this), 500);

    this.scrollableRef = null;
    this.labelRefs = [];
  }

  componentDidMount() {
    // Adds polyfill for window.scroll({params})
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/scroll#Browser_Compatibility
    smoothscroll.polyfill();
    window.addEventListener("scroll", this.throttledOnScroll);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.throttledOnScroll);
    this.throttledOnScroll.cancel();
  }

  getSanitizedProductLabels() {
    const { productLabels } = this.props;

    return productLabels.map(label => {
      return label.replace(/[^A-Za-z0-9]/gi, "");
    });
  }

  onScroll() {
    this.navigationOnScroll();
    this.setActiveState();
  }

  setActiveState() {
    const elementOffsets = [];

    const sanitizedProductLabels = this.getSanitizedProductLabels();

    sanitizedProductLabels.forEach(label => {
      const element = document.getElementById(label);
      const offsetFromTop =
        element.getBoundingClientRect().top - (mobileNavigationBuffer + 1);

      // We check if the distance from the element to top of viewport
      // is negative, if so we add it to array
      if (Math.sign(offsetFromTop) < 0) {
        elementOffsets.push(offsetFromTop);
      }
    });

    // Only visible elements are added to array, so we can use array.length
    // to determine active index :)
    const newIndex = elementOffsets.length ? elementOffsets.length - 1 : 0;
    const { selectedIndex } = this.state;

    // Wrapped in statement to prevent multiple renders when nothing changes
    if (selectedIndex !== newIndex) {
      this.setState({
        selectedIndex: newIndex,
      });

      // Scroll the Mobile Navigation to the newly active element.
      if (this.scrollableRef) {
        this.scrollableRef.scrollToIndex(newIndex);
      }
    }
  }

  navigationOnScroll() {
    const { floatNavigation } = this.state;

    const element = document.getElementById("ProductLandingCards");

    // Distance from top of element to top of viewport
    const topPosition = element.getBoundingClientRect().top;

    // Determines if we should `absolute: fixed` the navigation
    //
    // The 2nd part of this argument is negative as the element has
    // scrolled past the top of the viewport, so we check if its between
    // 8px from top of viewport and negative ElementHeight from top of viewport
    const shouldDisplayFixed =
      topPosition <= 8 && topPosition >= 0 - element.offsetHeight;

    // Wrapped in statements to protect setting state every px scrolled
    if (!floatNavigation && shouldDisplayFixed) {
      this.setState({
        floatNavigation: true,
      });
    } else if (floatNavigation && !shouldDisplayFixed) {
      this.setState({
        floatNavigation: false,
      });
    }
  }

  clickLabel(index) {
    const sanitizedProductLabels = this.getSanitizedProductLabels();
    const element = document.getElementById(sanitizedProductLabels[index]);
    const offset =
      element.getBoundingClientRect().top +
      (window.scrollY || window.pageYOffset); // IE fallback
    if (element && offset) {
      window.scroll({
        top: offset - mobileNavigationBuffer,
        behavior: "smooth",
      });
    }
  }

  render() {
    const { floatNavigation } = this.state;
    const { productLabels, products, dispatchAddProductToCart } = this.props;
    const sanitizedProductLabels = this.getSanitizedProductLabels();

    if (!products || !productLabels || !sanitizedProductLabels) return null;

    return (
      <Grid>
        <MobileNavigation className={floatNavigation ? "fixed" : ""}>
          <ScrollableOverflow
            ref={r => (this.scrollableRef = r)}
            bleedLeft={true}
            bleedRight={true}
          >
            {productLabels.map((label, index) => {
              let className =
                index === this.state.selectedIndex ? "active" : "";
              return (
                <MobileLabel
                  onClick={() => this.clickLabel(index)}
                  className={className}
                  key={index}
                  ref={r => (this.labelRefs[index] = r)}
                >
                  {label}
                </MobileLabel>
              );
            })}
          </ScrollableOverflow>
        </MobileNavigation>
        {products.map((product, index) => {
          return (
            <ProductCard
              id={sanitizedProductLabels[index]}
              key={NameMap(product.name).plain}
              index={index}
              product={product}
              dispatchAddProductToCart={dispatchAddProductToCart}
            />
          );
        })}
      </Grid>
    );
  }
}

const ProductCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 100%;
  margin: 0 0 40px;

  &:last-child {
    margin-bottom: 0;
  }

  ${responsive.sm`
    flex: 1 1 calc(50% - 20px);
    margin-right: 20px;
    margin-bottom: 0px;
    max-width: 280px;

    &:nth-child(2n+1) {
      margin-right: 0;
    }

    &:nth-child(n+4) {
      margin-top: 40px;
    }

    &:last-child {
      margin-right: 0;
    }
  `};

  ${responsive.md`
    flex: 0 0 calc(33% - (60px / 3));
    margin: 0 26px 0 0;
    max-width: 294px;

    &:nth-child(2n+1) {
      margin-right: 26px;
    }

    &:nth-child(n+4) {
      margin-top: 0;
    }

    &:nth-child(3n+1) {
      margin-right: 0;
    }

    &:nth-child(n+5) {
      margin-top: 80px;
    }

    &:last-child {
      margin-right: 0;
    }
  `};

  ${responsive.lg`
    max-width: 370px;
    margin: 0 30px 0 0;
  `};
`;

const ProductCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: rgba(242, 241, 245, 0.56);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 8px;
  max-width: 280px;

  ${responsive.sm`
    max-width: none;
    width: 280px;
  `};

  ${responsive.md`
    width: 294px;
    border-radius: 16px;
    padding: 24px 16px;
    margin-bottom: 16px;
  `};

  ${responsive.lg`
    width: 370px;
    padding: 24px 32px;
  `}
`;

const ProductTitle = styled.h2`
  ${Font.circular};
  font-size: ${rem(18)};
  line-height: ${rem(28)};
  font-weight: 500;
  text-align: center !important;
  margin: 0 0 4px;

  ${responsive.md`
    font-size: ${rem(22)};
    line-height: ${rem(32)};
    letter-spacing: -0.2px;
    margin: 0 0 12px;
  `};
`;

const ProductImage = styled(Img)`
  overflow: hidden;
  margin-bottom: 4px;
  height: 120px;
  width: 120px;
  max-height: 120px;
  max-width: 120px;

  ${responsive.md`
    width: 204px;
    height: 204px;
    max-height: 204px;
    max-width: 204px;
    margin-bottom: 8px;
  `};
`;

const ProductDescription = styled.p`
  ${Font.circular};
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  font-weight: 300;
  text-align: center !important;
  margin: 0 0 12px;

  ${responsive.md`
    font-size: ${rem(16)};
    line-height: ${rem(22)};
    font-weight: 300;
    margin: 0 0 16px;
  `};
`;

const LearnMore = styled(MagicLink)`
  font-size: ${rem(16)};
  line-height: ${rem(26)};
  font-weight: 500;
  border-bottom: 2px solid ${Color.ritualBlue};
  cursor: pointer;
  margin: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.56;
  }

  ${responsive.md`
    font-size: ${rem(18)};
    line-height: ${rem(28)};
  `};
`;

export class ProductCard extends React.Component {
  constructor() {
    super();

    this.state = {
      isLearnMoreVariation: false,
    };
  }

  componentDidMount() {
    this.setState({
      isLearnMoreVariation: variation("shop-landing-cta-swap"),
    });
  }

  handleVariationCTAClick(title, location, product) {
    this.trackCTAClick(title, location);

    const planId = getPlanIdForProductSku(product.sku);
    this.props.dispatchAddProductToCart(planId);
  }

  trackCTAClick(title, location) {
    metrics.track("CTA Clicked", {
      title: title,
      nonInteraction: false,
      location: location,
    });
  }

  trackLearnMoreClick(title, location, url, event) {
    event.preventDefault();
    metrics.track("CTA Clicked", {
      title: title,
      nonInteraction: false,
      location: location,
    });
    navigate(url);
  }

  render() {
    const { id, product } = this.props;

    // If the product is out of stock, do not update the variation.
    const isLearnMoreVariation =
      this.state.isLearnMoreVariation && product.stockStatus === "in stock";

    const productName = NameMap(product.name).plain;

    let ctaText = intl.t("general.button-add-cart", "Add to Cart");
    ctaText = `${intl.formatCurrency(product.price, { round: true })} — ${ctaText}`;

    const learnMoreLink = `/products/${product.slug}`;

    const checkoutButtonProps = {
      id: `${id}_button`,
      className: "yellow-cta",
      buttonText: ctaText,
      stockStatus: product.stockStatus,
      trackCTAClick: this.trackCTAClick.bind(
        this,
        ctaText,
        "Product List Page",
      ),
      product,
    };

    return (
      <ProductCardWrapper id={id}>
        <ProductCardContent>
          <ProductTitle id={`${id}_title`}>{product.summary}</ProductTitle>
          <ProductImage
            id={`${id}_image`}
            fixed={product.alternateThumbnail.fixed}
            fadeIn={true}
            alt={product.alternateThumbnail.description}
            style={{
              userSelect: "none",
              userDrag: "none",
              pointerEvents: "none",
              touchCallout: "none",
            }}
          />
          <ProductDescription id={`${id}_description`}>
            {product.shortDescription}
          </ProductDescription>

          {isLearnMoreVariation ? (
            <RitualButton
              id={`${id}_learn-more-button`}
              className="yellow-cta"
              to={learnMoreLink}
              onClick={event =>
                this.trackLearnMoreClick(
                  "Learn More",
                  "Product List Page",
                  learnMoreLink,
                  event,
                )
              }
              title={`Learn more about ${productName}`}
            >
              <Text id="general.learn-more" defaultMessage="Learn More" />
            </RitualButton>
          ) : (
            <CheckoutButton {...checkoutButtonProps} />
          )}
        </ProductCardContent>
        {isLearnMoreVariation ? (
          <LearnMore
            id={`${id}_link`}
            to="/cart"
            onClick={this.handleVariationCTAClick.bind(
              this,
              ctaText,
              "Product List Page",
              product,
            )}
          >
            {ctaText}
          </LearnMore>
        ) : (
          <LearnMore
            id={`${id}_learn-more-link`}
            to={learnMoreLink}
            onClick={event =>
              this.trackLearnMoreClick(
                "Learn More",
                "Product List Page",
                learnMoreLink,
                event,
              )
            }
            title={`Learn more about ${productName}`}
          >
            <Text id="general.learn-more" defaultMessage="Learn More" />
          </LearnMore>
        )}
      </ProductCardWrapper>
    );
  }
}

export default connect(null, {
  dispatchAddProductToCart: addProductToCart,
})(ProductCards);
